export type Dir = "asc" | "desc";

export interface Sort {
  key: string;
  dir: Dir;
}

export type SortValue = string | number | null | undefined;

export interface SortColumn<T> {
  key: string;
  label: string;
  value: (row: T) => SortValue;
  /** A fixed order of text values, for example statuses. Values not in it go after it. */
  order?: readonly string[];
  /** The direction of the first click. Numbers where large is urgent can start descending. */
  first?: Dir;
  /** Hidden until the user shows it in the Columns menu. */
  off?: boolean;
  /** A number column: the header and the cells align right. */
  num?: boolean;
  /** The hover help of the header (rule U-28). */
  help?: string;
  /** The desktop width, against the other shown columns. A column with no width in a table with widths takes the free width. */
  width?: number;
}

const text = new Intl.Collator("en-US", { numeric: true, sensitivity: "base" });

const empty = (value: SortValue) => value === null || value === undefined || value === "";

/** Compare two values in ascending order. Empty values are equal here; sortRows puts them last. */
export function compareValues(a: SortValue, b: SortValue, order?: readonly string[]): number {
  if (order) {
    const rank = (value: SortValue) => {
      const at = order.indexOf(String(value));
      return at < 0 ? order.length : at;
    };
    const diff = rank(a) - rank(b);
    if (diff) return diff;
  }
  if (typeof a === "number" && typeof b === "number") return a - b;
  return text.compare(String(a), String(b));
}

/** The truck order (D-4): the numbers first in number order, then the codes with letters, so 5, 6, 13, T1, T2, T10. */
export function compareTruck(a: string, b: string): number {
  return text.compare(a, b);
}

/** A copy of the rows in truck order. Rows with the same truck keep their order. */
export function byTruck<T>(rows: readonly T[], truck: (row: T) => string): T[] {
  return [...rows].sort((a, b) => compareTruck(truck(a), truck(b)));
}

/** Return a sorted copy. Empty values are always last, in both directions. Equal rows keep their order. */
export function sortRows<T>(rows: readonly T[], columns: readonly SortColumn<T>[], sort: Sort | null): T[] {
  const column = sort && columns.find((c) => c.key === sort.key);
  if (!sort || !column) return [...rows];
  const sign = sort.dir === "desc" ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index, value: column.value(row) }))
    .sort((a, b) => {
      const ea = empty(a.value);
      const eb = empty(b.value);
      if (ea || eb) return ea === eb ? a.index - b.index : ea ? 1 : -1;
      return sign * compareValues(a.value, b.value, column.order) || a.index - b.index;
    })
    .map(({ row }) => row);
}

/** A click on a header: the first click uses the first direction of the column, the next click turns it. */
export function nextSort<T>(sort: Sort | null, column: SortColumn<T>): Sort {
  if (sort?.key === column.key) return { key: column.key, dir: sort.dir === "asc" ? "desc" : "asc" };
  return { key: column.key, dir: column.first ?? "asc" };
}

export const ariaSort = (sort: Sort | null, key: string): "ascending" | "descending" | "none" =>
  sort?.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none";

/** The URL form is `key` for ascending and `-key` for descending. */
export function readSort<T>(search: string, param: string, columns: readonly SortColumn<T>[]): Sort | null {
  const raw = new URLSearchParams(search).get(param);
  if (!raw) return null;
  const dir: Dir = raw.startsWith("-") ? "desc" : "asc";
  const key = raw.replace(/^-/, "");
  return columns.some((c) => c.key === key) ? { key, dir } : null;
}

/** Return the search string with the sort set, or removed when there is no sort. Other parameters stay. */
export function writeSort(search: string, param: string, sort: Sort | null): string {
  const params = new URLSearchParams(search);
  if (sort) params.set(param, `${sort.dir === "desc" ? "-" : ""}${sort.key}`);
  else params.delete(param);
  const out = params.toString();
  return out ? `?${out}` : "";
}

/** The options of the phone "Sort by" select: each column in each direction. */
export function sortOptions<T>(columns: readonly SortColumn<T>[]): { value: string; label: string }[] {
  return columns.flatMap((c) => [
    { value: c.key, label: `${c.label}, ascending` },
    { value: `-${c.key}`, label: `${c.label}, descending` },
  ]);
}

export const sortParam = (sort: Sort | null) => (sort ? `${sort.dir === "desc" ? "-" : ""}${sort.key}` : "");

export function parseSortParam<T>(value: string, columns: readonly SortColumn<T>[]): Sort | null {
  return readSort(`?s=${encodeURIComponent(value)}`, "s", columns);
}
